// =============================================================================
// Cart page — qty steppers, row delete, server-driven price updates.
//
// Endpoints (set on the form via data-update-url / data-remove-url):
//   POST update-url  body: { id, qty }  → { err, msg, cart, unit_price, row_total, qty? }
//   POST remove-url  body: { id }       → { err, msg, cart }
//
// `unit_price` and `row_total` are decimal strings ("999.99"). The subtotal
// is computed client-side by summing every visible .js-rowTotal — the server
// is the source of truth for individual row pricing (tier discounts, taxes,
// stock-trimmed qty come back via the response).
// =============================================================================
(function ($) {
    'use strict';

    const QTY_DEBOUNCE_MS = 350;

    function formatMoney(input) {
        let value;
        if (typeof input === 'number') {
            value = input;
        } else if (typeof input === 'string') {
            value = parseFloat(input.replace(/,/g, ''));
        } else {
            value = NaN;
        }
        if (!Number.isFinite(value)) { value = 0; }
        return 'CAD $' + value.toLocaleString('en-CA', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    function parseMoney(text) {
        if (text === null || text === undefined) { return 0; }
        const numeric = parseFloat(String(text).replace(/[^0-9.-]+/g, ''));
        return Number.isFinite(numeric) ? numeric : 0;
    }

    function getQty($input) {
        const min = parseInt($input.attr('min'), 10) || 1;
        const v = parseInt($input.val(), 10);
        return Number.isFinite(v) && v >= min ? v : min;
    }

    function recalcSubtotal($form) {
        let sub = 0;
        $form.find('.js-cartRow .js-rowTotal').each(function () {
            sub += parseMoney(this.textContent);
        });
        $form.find('.js-cartSubtotal').text(formatMoney(sub));
    }

    function applyRowUpdate($row, res) {
        if (!res) { return; }

        if (res.unit_price !== undefined && res.unit_price !== null) {
            $row.find('.js-rowUnitPrice').text(formatMoney(res.unit_price));
            $row.attr('data-price', String(res.unit_price));
        }

        if (res.row_total !== undefined && res.row_total !== null) {
            $row.find('.js-rowTotal').text(formatMoney(res.row_total));
        }

        // Server may trim qty (e.g. stock limit) — sync the input back.
        if (res.qty !== undefined && res.qty !== null) {
            const $input = $row.find('.js-qtyInput');
            const serverQty = parseInt(res.qty, 10);
            if (Number.isFinite(serverQty) && String(serverQty) !== $input.val()) {
                $input.val(serverQty);
            }
        }
    }

    function checkEmpty($form) {
        const rows = $form.find('.js-cartRow').length;
        const $body = $form.find('.js-cartBody');
        if (rows === 0 && $body.length && !$form.find('.cart-form__empty').length) {
            $form.find('.js-cartTable').after(
                '<p class="cart-form__empty">Your cart is empty.</p>'
            );
            $form.find('.js-cartTable').hide();
            $form.find('.js-formSubmit').prop('disabled', true).addClass('btn--disabled');
        }
    }

    function postUpdate($row) {
        const $form = $row.closest('form');
        const url = $form.attr('data-update-url');
        if (!url) { return; }

        const id = $row.attr('data-id');
        const qty = getQty($row.find('.js-qtyInput'));

        // Latest qty wins — abort any in-flight request for the same row.
        const inflight = $row.data('updateXhr');
        if (inflight && inflight.abort) { inflight.abort(); }

        $row.addClass('is-updating');

        const xhr = $.ajax({
            method: 'POST',
            url,
            data: { id, qty },
            dataType: 'json',
        })
            .done((res) => {
                if (res && (res.cart || res.cart === 0)) {
                    $('.cart-count').text(res.cart);
                }

                if (res && !res.err) {
                    applyRowUpdate($row, res);
                    recalcSubtotal($form);
                }

                if (res && res.err && res.msg && window.notyf) {
                    window.notyf.error(res.msg);
                }
            })
            .fail((jqXhr, textStatus) => {
                if (textStatus !== 'abort' && window.notyf) {
                    window.notyf.error('Could not update quantity.');
                }
            })
            .always(() => {
                $row.removeClass('is-updating');
            });

        $row.data('updateXhr', xhr);
    }

    function postRemove($row, id) {
        const $form = $row.closest('form');
        const url = $form.attr('data-remove-url');
        if (!url) { return; }

        $.ajax({
            method: 'POST',
            url,
            data: { id },
            dataType: 'json',
        })
            .done((res) => {
                if (res && (res.cart || res.cart === 0)) {
                    $('.cart-count').text(res.cart);
                }
                if (res && res.err && res.msg && window.notyf) {
                    window.notyf.error(res.msg);
                }
            })
            .fail(() => {
                if (window.notyf) { window.notyf.error('Could not remove item.'); }
            });
    }

    function debouncedPushQty($row) {
        clearTimeout($row.data('qtyTimer'));
        const t = setTimeout(() => postUpdate($row), QTY_DEBOUNCE_MS);
        $row.data('qtyTimer', t);
    }

    $(function () {
        const tableSel = '.js-cartTable';

        $(document).on('click', tableSel + ' .js-qtyDec', function () {
            const $row = $(this).closest('.js-cartRow');
            const $input = $row.find('.js-qtyInput');
            const min = parseInt($input.attr('min'), 10) || 1;
            const next = Math.max(min, getQty($input) - 1);
            if (next === getQty($input)) { return; }
            $input.val(next);
            debouncedPushQty($row);
        });

        $(document).on('click', tableSel + ' .js-qtyInc', function () {
            const $row = $(this).closest('.js-cartRow');
            const $input = $row.find('.js-qtyInput');
            $input.val(getQty($input) + 1);
            debouncedPushQty($row);
        });

        $(document).on('input', tableSel + ' .js-qtyInput', function () {
            debouncedPushQty($(this).closest('.js-cartRow'));
        });

        // On blur: sanitise the input then flush any pending push immediately.
        $(document).on('change blur', tableSel + ' .js-qtyInput', function () {
            const $row = $(this).closest('.js-cartRow');
            const $input = $row.find('.js-qtyInput');
            const min = parseInt($input.attr('min'), 10) || 1;
            const v = parseInt($input.val(), 10);
            if (!Number.isFinite(v) || v < min) { $input.val(min); }
            clearTimeout($row.data('qtyTimer'));
            postUpdate($row);
        });

        $(document).on('click', tableSel + ' .js-deleteRow', function () {
            const $row = $(this).closest('.js-cartRow');
            const $form = $row.closest('form');
            const id = $row.attr('data-id');

            $row.addClass('is-removing');
            postRemove($row, id);

            setTimeout(() => {
                $row.remove();
                recalcSubtotal($form);
                checkEmpty($form);
            }, 200);
        });

        // Sync subtotal on load — handles SSR rounding mismatches.
        $('.js-cartForm').each(function () { recalcSubtotal($(this)); });
    });
})(jQuery);
